import React from "react";
import { TextField, FormControl, Tooltip } from "@mui/material";
import { TextFieldProps } from "@mui/material/TextField";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import enUS from "date-fns/locale/en-US";
import { format, parse, isValid } from "date-fns";
import theme from "../../themes/index.en";
import { GlobalStyles } from "@mui/system";
import { CustomDatePickerProps } from "./customDatePickerProps";
import { INVALID_DATE_FORMAT } from "../../constants/actions";

const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  value,
  label,
  onChange,
  onBlur,
  required,
  error,
  disabled,
  helperText,
  isRTL = false,
}) => {
  const [localError, setLocalError] = React.useState(false);

  const handleDateChange = (date: Date | null) => {
    if (date && isValid(date)) {
      setLocalError(false); 
      onChange(format(date, "dd/MM/yyyy"));
    } else {
      setLocalError(true); 
      onChange("");  
    }
  };

  let parsedValue = value ? parse(value, "dd/MM/yyyy", new Date()) : null;
  if (parsedValue && !isValid(parsedValue)) {
    parsedValue = null;
  }

  const customLabel = (
    <Tooltip title={label} placement="top" arrow>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: isRTL ? "flex-end" : "flex-start",
          direction: isRTL ? "rtl" : "ltr",
          textAlign: isRTL ? "right" : "left",
          maxWidth: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
        {required && (
          <span
            style={{
              marginLeft: "5px",
              color: theme.palette.error.main,
              direction: isRTL ? "rtl" : "ltr",
              textAlign: isRTL ? "right" : "left",
            }}
          >
            *
          </span>
        )}
      </span>
    </Tooltip>
  );

  return (
    <FormControl
      fullWidth
      margin="normal"
      error={error || localError}
      sx={{
        direction: isRTL ? "rtl" : "ltr",
        textAlign: isRTL ? "right" : "left",
      }}
    >
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUS}>
        <GlobalStyles
          styles={{
            ".MuiTypography-root.MuiTypography-caption": {
              color: theme.palette.text.primary,
            },
          }}
        />
        <DatePicker
          label={customLabel}
          value={parsedValue}
          disabled={disabled}
          inputFormat="dd/MM/yyyy" 
          onClose={() => {
            if (onBlur) {
              onBlur();
            }
          }}
          onChange={handleDateChange}
          renderInput={(params: TextFieldProps) => (
            <TextField
              {...params}
              disabled={disabled}
              error={error || localError}
              helperText={localError ? INVALID_DATE_FORMAT : helperText || ""}
              InputLabelProps={{
                shrink: true,
                sx: {
                  textAlign: isRTL ? "right" : "left",
                  right: isRTL ? 0 : "auto",
                  left: isRTL ? "auto" : 0,
                },
              }}
              FormHelperTextProps={{
                style: {
                  color: theme.palette.error.main,
                  textAlign: isRTL ? "right" : "left",
                },
              }}
              sx={{
                "& .MuiInputBase-root": {
                  backgroundColor: theme.palette.background.default,
                  direction: isRTL ? "rtl" : "ltr",
                  textAlign: isRTL ? "right" : "left",
                },
                "& .MuiInputAdornment-root": {
                  marginLeft: isRTL ? "0" : "8px",
                  marginRight: isRTL ? "8px" : "0",
                },
              }}
            />
          )}
        />
      </LocalizationProvider>
    </FormControl>
  );
};


export default CustomDatePicker;
